/**
 * Trends Analytics Dashboard Page
 *
 * Dashboard completo de análisis de tendencias con:
 * - Hashtags trending
 * - Mejores horarios de publicación
 * - Formatos más efectivos
 * - Insights de IA
 */

import React, { useState, useEffect } from 'react';
import { FiTrendingUp, FiHash, FiClock, FiImage, FiRefreshCw, FiFilter, FiCpu, FiGlobe, FiSettings } from 'react-icons/fi';
import trendsService from '../services/trendsService';
import CategoryManagementModal from '../components/CategoryManagementModal';
import Loading from '../components/Loading';
import './TrendsPage.css';

const TrendsPage = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [platform, setPlatform] = useState('instagram');
  const [activeTab, setActiveTab] = useState('google'); // google, insights

  // Google Trends category filter
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showCategoryModal, setShowCategoryModal] = useState(false);

  const [dashboardData, setDashboardData] = useState({
    summary: {
      total_recommendations: 0,
      total_hashtags: 0,
      total_posting_times: 0,
      total_content_formats: 0,
      avg_estimated_engagement: 0,
      last_updated: null,
    },
    hashtags: [],
    posting_times: [],
    content_formats: [],
    recommendations: [],
    google_trends: [],
    ai_insights: {},
    visual_insights: {},
  });

  useEffect(() => {
    fetchDashboard();
    fetchCategories();
  }, [platform]);


  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const data = await trendsService.getDashboard(platform);
      setDashboardData(data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await trendsService.getCategories();
      setCategories(data.results || data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await trendsService.refreshRecommendations(platform, 30);
      await fetchDashboard();
    } catch (error) {
      console.error('Error refreshing:', error);
      alert('Error al regenerar recomendaciones');
    } finally {
      setRefreshing(false);
    }
  };

  const handleGenerateAI = async () => {
    try {
      setGeneratingAI(true);
      const result = await trendsService.generateAIInsights(platform);

      if (result.status === 'success') {
        // Reload dashboard to show new insights
        await fetchDashboard();
        alert(`✅ ${result.message}`);
      } else {
        alert(`⚠️ ${result.message}`);
      }
    } catch (error) {
      console.error('Error generating AI insights:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Error desconocido';
      alert(`❌ Error al generar AI Insights: ${errorMsg}`);
    } finally {
      setGeneratingAI(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return <Loading />;
  }

  const { summary, hashtags, posting_times, content_formats, google_trends, ai_insights, visual_insights } = dashboardData;

  // Filter Google Trends by selected category
  const filteredGoogleTrends = selectedCategory === 'all'
    ? (google_trends || [])
    : (google_trends || []).filter(trend => trend.category === parseInt(selectedCategory));

  return (
    <div className="trends-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <FiTrendingUp size={32} className="header-icon" />
          <div>
            <h1>Predictor de Tendencias</h1>
            <p className="subtitle">
              Última actualización: {formatDate(summary.last_updated)}
            </p>
          </div>
        </div>
        <div className="header-actions">
          <button
            className="estado-btn"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <FiRefreshCw className={refreshing ? 'spinning' : ''} />
            {refreshing ? 'Actualizando...' : 'Actualizar'}
          </button>
        </div>
      </div>

      {/* Stats Grid */}

      {/* Tabs */}
      <div className="tabs">
        <button
          className={activeTab === 'google' ? 'active' : ''}
          onClick={() => setActiveTab('google')}
        >
          <FiGlobe /> Google Trends
        </button>
        <button
          className={activeTab === 'insights' ? 'active' : ''}
          onClick={() => setActiveTab('insights')}
        >
          <FiCpu /> Insights IA
        </button>
      </div>

      {/* Content */}
      <div className="content">

        {/* Google Trends Section */}
        {activeTab === 'google' && (
          <div className="section">
            <div className="google-trends-header">
              <div className="google-trends-title-section">
                <h2>
                  <FiGlobe /> Tendencias de Google
                </h2>
                <p className="section-description">
                  Tendencias de búsqueda en Google para Chile (últimos 7 días)
                </p>
              </div>

              {/* Category Filter */}
              <div className="google-trends-filters">
                {categories.length > 0 && (
                  <>
                    <label htmlFor="category-filter" className="category-filter-label">
                      Categoría:
                    </label>
                    <select
                      id="category-filter"
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="category-filter-select"
                    >
                      <option value="all">Todas las categorías</option>
                      {categories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </>
                )}
                <button
                  onClick={() => setShowCategoryModal(true)}
                  className="btn-manage-categories"
                  title="Gestionar categorías de Google Trends"
                >
                  <FiSettings size={16} />
                  Gestionar
                </button>
              </div>
            </div>

            <div className="google-trends-grid">
              {filteredGoogleTrends.slice(0, 10).map((trend) => (
                <div key={trend.id} className="google-trend-card">
                  <div className="trend-header">
                    <span className="trend-keyword">{trend.keyword}</span>
                    <span className={`badge ${trend.interest_score > 50 ? 'high' : trend.interest_score > 20 ? 'medium' : 'low'}`}>
                      Score: {trend.interest_score}
                    </span>
                  </div>

                  {trend.category_name && (
                    <div className="trend-category">
                      <span className="category-tag">{trend.category_name}</span>
                    </div>
                  )}

                  <div className="trend-meta">
                    <span className="trend-date">
                      {new Date(trend.snapshot_date).toLocaleDateString('es-CL', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </span>
                    <span className="trend-geo">📍 {trend.geo || 'CL'}</span>
                  </div>

                  {/* Related Queries */}
                  {trend.related_queries && (trend.related_queries.top?.length > 0 || trend.related_queries.rising?.length > 0) && (
                    <div className="related-queries">
                      {trend.related_queries.rising && trend.related_queries.rising.length > 0 && (
                        <div className="queries-section">
                          <h4>🔥 En Ascenso:</h4>
                          <div className="query-tags">
                            {trend.related_queries.rising.slice(0, 5).map((query, idx) => (
                              <span key={idx} className="query-tag rising">
                                {query.query || query}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {trend.related_queries.top && trend.related_queries.top.length > 0 && (
                        <div className="queries-section">
                          <h4>⭐ Más Buscados:</h4>
                          <div className="query-tags">
                            {trend.related_queries.top.slice(0, 5).map((query, idx) => (
                              <span key={idx} className="query-tag top">
                                {query.query || query}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {filteredGoogleTrends.length === 0 && (
              <div className="empty-state">
                <FiGlobe size={48} />
                <p>
                  {selectedCategory === 'all'
                    ? 'No hay tendencias de Google disponibles'
                    : 'No hay tendencias para esta categoría'
                  }
                </p>
                <p className="hint">
                  {selectedCategory === 'all'
                    ? 'Crea una TrendCategory con keywords para comenzar a trackear tendencias de Google.'
                    : 'Cambia la categoría o agrega keywords a esta categoría.'
                  }
                </p>
              </div>
            )}
          </div>
        )}

        {/* AI Insights Section */}
        {activeTab === 'insights' && (
          <div className="section ai-insights-section">
            <div className="section-header-with-action">
              <h2>
                <FiCpu /> Insights de Inteligencia Artificial
              </h2>
              {Object.keys(ai_insights).length === 0 && (
                <button
                  className="btn-generate-ai"
                  onClick={handleGenerateAI}
                  disabled={generatingAI}
                >
                  <FiCpu className={generatingAI ? 'spinning' : ''} />
                  {generatingAI ? 'Generando...' : 'Generar AI Insights'}
                </button>
              )}
            </div>

            {/* Empty state */}
            {Object.keys(ai_insights).length === 0 && !generatingAI && (
              <div className="empty-state">
                <FiCpu size={48} />
                <p>No hay insights de IA disponibles</p>
                <p className="hint">
                  Haz click en "Generar AI Insights" para analizar tus contenidos con inteligencia artificial.
                </p>
              </div>
            )}

            {/* Loading state */}
            {generatingAI && (
              <div className="loading-state">
                <Loading fullScreen={false} mensaje="Generando insights con IA" />
                <p className="hint">Esto puede tomar 1-2 minutos</p>
              </div>
            )}

            {/* Insights content (only when available) */}
            {Object.keys(ai_insights).length > 0 && (
              <>

            {/* Caption Analysis */}
            {ai_insights.caption_analysis && (
              <div className="insight-card">
                <h3>Análisis de Captions Exitosos</h3>
                {ai_insights.caption_analysis.common_themes && (
                  <div className="themes">
                    <h4>Temas Comunes:</h4>
                    <div className="theme-tags">
                      {ai_insights.caption_analysis.common_themes.map((theme, idx) => (
                        <span key={idx} className="theme-tag">{theme}</span>
                      ))}
                    </div>
                  </div>
                )}
                {ai_insights.caption_analysis.recommendations && (
                  <div className="recommendations">
                    <h4>Recomendaciones:</h4>
                    <ul>
                      {ai_insights.caption_analysis.recommendations.map((rec, idx) => (
                        <li key={idx}>{rec}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Trend Explanations */}
            {ai_insights.trend_explanations && ai_insights.trend_explanations.length > 0 && (
              <div className="insight-card">
                <h3>Explicación de Tendencias</h3>
                {ai_insights.trend_explanations.map((exp, idx) => (
                  <div key={idx} className="trend-explanation">
                    <h4>{exp.keyword || exp.trend_name}</h4>
                    <p>{exp.explanation}</p>
                    {exp.opportunity && (
                      <div className="opportunity-box">
                        <strong>Oportunidad:</strong> {exp.opportunity}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Competitor Analysis */}
            {ai_insights.competitor_analysis && (
              <div className="insight-card">
                <h3>Análisis de Competencia</h3>
                {ai_insights.competitor_analysis.gaps_and_opportunities && (
                  <div className="opportunities">
                    <h4>Oportunidades Detectadas:</h4>
                    <ul>
                      {ai_insights.competitor_analysis.gaps_and_opportunities.map((gap, idx) => (
                        <li key={idx} className="opportunity-item">{gap}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Content Format Performance */}
            {ai_insights.content_format_performance && (
              <div className="insight-card">
                <h3>📊 Rendimiento por Formato de Contenido</h3>
                {ai_insights.content_format_performance.format_performance && (
                  <div className="format-performance">
                    <p className="summary">{ai_insights.content_format_performance.format_performance.summary}</p>
                    <div className="best-format">
                      <strong>Mejor Formato:</strong> {ai_insights.content_format_performance.best_format}
                    </div>
                    {ai_insights.content_format_performance.recommendations && (
                      <div className="recommendations">
                        <h4>Recomendaciones:</h4>
                        <ul>
                          {ai_insights.content_format_performance.recommendations.map((rec, idx) => (
                            <li key={idx}>{rec}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Timing Optimization */}
            {ai_insights.timing_optimization && (
              <div className="insight-card">
                <h3>⏰ Optimización de Horarios</h3>
                {ai_insights.timing_optimization.optimal_schedule && (
                  <div className="timing-schedule">
                    <h4>Horarios Óptimos por Día:</h4>
                    <div className="schedule-grid">
                      {Object.entries(ai_insights.timing_optimization.optimal_schedule).map(([day, data]) => (
                        <div key={day} className="schedule-item">
                          <strong>{day.charAt(0).toUpperCase() + day.slice(1)}:</strong> {data.best_time} ({data.format})
                        </div>
                      ))}
                    </div>
                    {ai_insights.timing_optimization.insights && (
                      <p className="timing-insights">{ai_insights.timing_optimization.insights}</p>
                    )}
                    {ai_insights.timing_optimization.recommendations && (
                      <div className="recommendations">
                        <h4>Recomendaciones:</h4>
                        <ul>
                          {ai_insights.timing_optimization.recommendations.map((rec, idx) => (
                            <li key={idx}>{rec}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Audience Growth Correlation */}
            {ai_insights.audience_growth_correlation && (
              <div className="insight-card">
                <h3>📈 Correlación con Crecimiento de Audiencia</h3>
                {ai_insights.audience_growth_correlation.growth_drivers && (
                  <div className="growth-analysis">
                    <div className="growth-drivers">
                      <h4>Drivers de Crecimiento:</h4>
                      <ul>
                        {ai_insights.audience_growth_correlation.growth_drivers.map((driver, idx) => (
                          <li key={idx}>{driver}</li>
                        ))}
                      </ul>
                    </div>
                    {ai_insights.audience_growth_correlation.follower_velocity && (
                      <div className="velocity">
                        <strong>Velocidad de Crecimiento:</strong> {ai_insights.audience_growth_correlation.follower_velocity}
                      </div>
                    )}
                    {ai_insights.audience_growth_correlation.recommendations && (
                      <div className="recommendations">
                        <h4>Recomendaciones:</h4>
                        <ul>
                          {ai_insights.audience_growth_correlation.recommendations.map((rec, idx) => (
                            <li key={idx}>{rec}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Engagement Rate Benchmarking */}
            {ai_insights.engagement_rate_benchmarking && (
              <div className="insight-card">
                <h3>🎯 Benchmarking de Engagement Rate</h3>
                <div className="engagement-benchmark">
                  <div className="benchmark-stats">
                    <div className="stat-item">
                      <strong>Tu Engagement Rate:</strong> {ai_insights.engagement_rate_benchmarking.your_engagement_rate}
                    </div>
                    {ai_insights.engagement_rate_benchmarking.industry_benchmark && (
                      <div className="stat-item">
                        <strong>Benchmark de Industria:</strong> {ai_insights.engagement_rate_benchmarking.industry_benchmark}
                      </div>
                    )}
                  </div>
                  {ai_insights.engagement_rate_benchmarking.top_performer && (
                    <div className="top-performer">
                      <h4>Mejor Desempeño:</h4>
                      <p><strong>{ai_insights.engagement_rate_benchmarking.top_performer.username}</strong> - {ai_insights.engagement_rate_benchmarking.top_performer.engagement_rate}</p>
                      {ai_insights.engagement_rate_benchmarking.top_performer.success_factors && (
                        <ul>
                          {ai_insights.engagement_rate_benchmarking.top_performer.success_factors.map((factor, idx) => (
                            <li key={idx}>{factor}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                  {ai_insights.engagement_rate_benchmarking.recommendations && (
                    <div className="recommendations">
                      <h4>Recomendaciones:</h4>
                      <ul>
                        {ai_insights.engagement_rate_benchmarking.recommendations.map((rec, idx) => (
                          <li key={idx}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Caption Structure Analysis */}
            {ai_insights.caption_structure_analysis && (
              <div className="insight-card">
                <h3>✍️ Análisis de Estructura de Captions</h3>
                <div className="caption-structure">
                  <div className="structure-insights">
                    {ai_insights.caption_structure_analysis.optimal_caption_length && (
                      <div className="insight-item">
                        <strong>Longitud Óptima:</strong> {ai_insights.caption_structure_analysis.optimal_caption_length}
                      </div>
                    )}
                    {ai_insights.caption_structure_analysis.best_structure && (
                      <div className="insight-item">
                        <strong>Mejor Estructura:</strong> {ai_insights.caption_structure_analysis.best_structure}
                      </div>
                    )}
                    {ai_insights.caption_structure_analysis.emoji_insights && (
                      <div className="insight-item">
                        <strong>Insights de Emojis:</strong> {ai_insights.caption_structure_analysis.emoji_insights}
                      </div>
                    )}
                    {ai_insights.caption_structure_analysis.hashtag_sweet_spot && (
                      <div className="insight-item">
                        <strong>Número Óptimo de Hashtags:</strong> {ai_insights.caption_structure_analysis.hashtag_sweet_spot}
                      </div>
                    )}
                  </div>
                  {ai_insights.caption_structure_analysis.recommendations && (
                    <div className="recommendations">
                      <h4>Recomendaciones:</h4>
                      <ul>
                        {ai_insights.caption_structure_analysis.recommendations.map((rec, idx) => (
                          <li key={idx}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Competitor Positioning */}
            {ai_insights.competitor_positioning && (
              <div className="insight-card">
                <h3>🎭 Posicionamiento Competitivo</h3>
                <div className="competitor-positioning">
                  {ai_insights.competitor_positioning.competitor_positioning && (
                    <div className="competitors-list">
                      <h4>Análisis de Competidores:</h4>
                      {ai_insights.competitor_positioning.competitor_positioning.map((comp, idx) => (
                        <div key={idx} className="competitor-item">
                          <strong>{comp.username}</strong> - {comp.positioning}
                          {comp.approach && <p><em>{comp.approach}</em></p>}
                        </div>
                      ))}
                    </div>
                  )}
                  {ai_insights.competitor_positioning.market_gaps && (
                    <div className="market-gaps">
                      <h4>Gaps de Mercado:</h4>
                      <ul>
                        {ai_insights.competitor_positioning.market_gaps.map((gap, idx) => (
                          <li key={idx}>{gap}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {ai_insights.competitor_positioning.differentiation_opportunity && (
                    <div className="differentiation">
                      <strong>Oportunidad de Diferenciación:</strong>
                      <p>{ai_insights.competitor_positioning.differentiation_opportunity}</p>
                    </div>
                  )}
                  {ai_insights.competitor_positioning.strategic_recommendations && (
                    <div className="recommendations">
                      <h4>Recomendaciones Estratégicas:</h4>
                      <ul>
                        {ai_insights.competitor_positioning.strategic_recommendations.map((rec, idx) => (
                          <li key={idx}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Viral Content Prediction */}
            {ai_insights.viral_content_prediction && (
              <div className="insight-card">
                <h3>🚀 Predicción de Contenido Viral</h3>
                <div className="viral-prediction">
                  {ai_insights.viral_content_prediction.viral_indicators && (
                    <div className="viral-indicators">
                      <h4>Indicadores de Viralidad:</h4>
                      <ul>
                        {ai_insights.viral_content_prediction.viral_indicators.map((indicator, idx) => (
                          <li key={idx}>{indicator}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {ai_insights.viral_content_prediction.next_viral_opportunity && (
                    <div className="next-opportunity">
                      <h4>Próxima Oportunidad Viral:</h4>
                      <p><strong>Tema:</strong> {ai_insights.viral_content_prediction.next_viral_opportunity.topic}</p>
                      <p><strong>Formato:</strong> {ai_insights.viral_content_prediction.next_viral_opportunity.format}</p>
                      <p><strong>Mejor Momento:</strong> {ai_insights.viral_content_prediction.next_viral_opportunity.best_time}</p>
                      {ai_insights.viral_content_prediction.next_viral_opportunity.estimated_reach && (
                        <p><strong>Alcance Estimado:</strong> {ai_insights.viral_content_prediction.next_viral_opportunity.estimated_reach}</p>
                      )}
                    </div>
                  )}
                  {ai_insights.viral_content_prediction.recommendations && (
                    <div className="recommendations">
                      <h4>Recomendaciones:</h4>
                      <ul>
                        {ai_insights.viral_content_prediction.recommendations.map((rec, idx) => (
                          <li key={idx}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Seasonal Trends */}
            {ai_insights.seasonal_trends && (
              <div className="insight-card">
                <h3>🌸 Tendencias Estacionales</h3>
                <div className="seasonal-trends">
                  {ai_insights.seasonal_trends.high_engagement_periods && (
                    <div className="high-periods">
                      <h4>Períodos de Alto Engagement:</h4>
                      {ai_insights.seasonal_trends.high_engagement_periods.map((period, idx) => (
                        <div key={idx} className="period-item">
                          <strong>{period.period}:</strong> {period.increase}
                          <p>{period.reason}</p>
                          {period.content_suggestions && (
                            <ul>
                              {period.content_suggestions.map((suggestion, sidx) => (
                                <li key={sidx}>{suggestion}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {ai_insights.seasonal_trends.content_calendar_suggestions && (
                    <div className="calendar-suggestions">
                      <h4>Sugerencias de Calendario:</h4>
                      <ul>
                        {ai_insights.seasonal_trends.content_calendar_suggestions.map((suggestion, idx) => (
                          <li key={idx}>{suggestion}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {ai_insights.seasonal_trends.recommendations && (
                    <div className="recommendations">
                      <h4>Recomendaciones:</h4>
                      <ul>
                        {ai_insights.seasonal_trends.recommendations.map((rec, idx) => (
                          <li key={idx}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ========== VISUAL INSIGHTS SECTION ========== */}
            {Object.keys(visual_insights).length > 0 && (
              <>
                <div className="section-divider">
                  <h2 className="section-divider-title">🎨 Análisis Visual con IA</h2>
                  <p className="section-divider-subtitle">Insights sobre elementos visuales, colores y composición</p>
                </div>

                {/* Visual Composition */}
                {visual_insights.visual_composition && (
                  <div className="insight-card visual-card">
                    <h3>🖼️ Composición Visual</h3>
                    <div className="visual-composition">
                      {visual_insights.visual_composition.dominant_colors && (
                        <div className="color-palette">
                          <h4>Colores Dominantes:</h4>
                          <div className="color-swatches">
                            {visual_insights.visual_composition.dominant_colors.map((color, idx) => (
                              <div key={idx} className="color-swatch-item">
                                <div
                                  className="color-swatch"
                                  style={{ backgroundColor: color }}
                                  title={color}
                                ></div>
                                <span className="color-code">{color}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {visual_insights.visual_composition.visual_elements && (
                        <div className="visual-elements">
                          <h4>Elementos Visuales Principales:</h4>
                          <div className="element-tags">
                            {visual_insights.visual_composition.visual_elements.map((element, idx) => (
                              <span key={idx} className="element-tag">{element}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {visual_insights.visual_composition.composition_style && (
                        <div className="composition-style">
                          <h4>Estilo de Composición:</h4>
                          <p>{visual_insights.visual_composition.composition_style}</p>
                        </div>
                      )}
                      {visual_insights.visual_composition.mood_and_tone && (
                        <div className="mood-tone">
                          <strong>Mood & Tone:</strong> {visual_insights.visual_composition.mood_and_tone}
                        </div>
                      )}
                      {visual_insights.visual_composition.recommendations && (
                        <div className="recommendations">
                          <h4>Recomendaciones:</h4>
                          <ul>
                            {visual_insights.visual_composition.recommendations.map((rec, idx) => (
                              <li key={idx}>{rec}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Color Palette Performance */}
                {visual_insights.color_palette_performance && (
                  <div className="insight-card visual-card">
                    <h3>🎨 Rendimiento de Paletas de Color</h3>
                    <div className="color-performance">
                      {visual_insights.color_palette_performance.top_performing_colors && (
                        <div className="top-colors">
                          <h4>Colores con Mejor Engagement:</h4>
                          <div className="color-performance-list">
                            {visual_insights.color_palette_performance.top_performing_colors.map((color, idx) => (
                              <div key={idx} className="color-performance-item">
                                <div
                                  className="color-preview"
                                  style={{ backgroundColor: color.color_hex }}
                                ></div>
                                <div className="color-info">
                                  <strong>{color.color_name}</strong>
                                  <span className="color-hex">{color.color_hex}</span>
                                  {color.avg_engagement && (
                                    <span className="color-engagement">Avg: {color.avg_engagement} engagement</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {visual_insights.color_palette_performance.color_combinations && (
                        <div className="color-combinations">
                          <h4>Combinaciones Exitosas:</h4>
                          {visual_insights.color_palette_performance.color_combinations.map((combo, idx) => (
                            <div key={idx} className="combo-item">
                              <div className="combo-palette">
                                {combo.palette && combo.palette.map((c, cidx) => (
                                  <div key={cidx} className="combo-color" style={{ backgroundColor: c }}></div>
                                ))}
                              </div>
                              <div className="combo-details">
                                <strong>{combo.description}</strong>
                                {combo.performance_score && (
                                  <span className="score">Score: {combo.performance_score}/100</span>
                                )}
                                {combo.emotional_impact && (
                                  <p className="emotional">{combo.emotional_impact}</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {visual_insights.color_palette_performance.recommendations && (
                        <div className="recommendations">
                          <h4>Recomendaciones:</h4>
                          <ul>
                            {visual_insights.color_palette_performance.recommendations.map((rec, idx) => (
                              <li key={idx}>{rec}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Visual Trends */}
                {visual_insights.visual_trends && (
                  <div className="insight-card visual-card">
                    <h3>📈 Tendencias Visuales Emergentes</h3>
                    <div className="visual-trends">
                      {visual_insights.visual_trends.trending_styles && (
                        <div className="trending-styles">
                          <h4>Estilos Trending:</h4>
                          {visual_insights.visual_trends.trending_styles.map((style, idx) => (
                            <div key={idx} className="trend-style-item">
                              <div className="style-header">
                                <strong>{style.style}</strong>
                                {style.growth_rate && (
                                  <span className="growth-badge">+{style.growth_rate}%</span>
                                )}
                              </div>
                              {style.description && <p className="style-desc">{style.description}</p>}
                              {style.why_its_working && (
                                <p className="why-working"><em>Por qué funciona:</em> {style.why_its_working}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {visual_insights.visual_trends.gaps_and_opportunities && (
                        <div className="opportunities">
                          <h4>Oportunidades Detectadas:</h4>
                          <ul>
                            {visual_insights.visual_trends.gaps_and_opportunities.map((gap, idx) => (
                              <li key={idx} className="opportunity-item">{gap}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {visual_insights.visual_trends.next_visual_trend && (
                        <div className="next-trend">
                          <h4>🔮 Próxima Tendencia Visual:</h4>
                          <div className="trend-prediction">
                            <p><strong>Predicción:</strong> {visual_insights.visual_trends.next_visual_trend.prediction}</p>
                            {visual_insights.visual_trends.next_visual_trend.timing && (
                              <p><strong>Timing:</strong> {visual_insights.visual_trends.next_visual_trend.timing}</p>
                            )}
                            {visual_insights.visual_trends.next_visual_trend.how_to_implement && (
                              <p><strong>Cómo implementar:</strong> {visual_insights.visual_trends.next_visual_trend.how_to_implement}</p>
                            )}
                          </div>
                        </div>
                      )}
                      {visual_insights.visual_trends.recommendations && (
                        <div className="recommendations">
                          <h4>Recomendaciones:</h4>
                          <ul>
                            {visual_insights.visual_trends.recommendations.map((rec, idx) => (
                              <li key={idx}>{rec}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Visual Engagement Prediction */}
                {visual_insights.visual_engagement_prediction && (
                  <div className="insight-card visual-card">
                    <h3>⚡ Predicción de Engagement Visual</h3>
                    <div className="engagement-prediction">
                      {visual_insights.visual_engagement_prediction.predicted_engagement_score && (
                        <div className="prediction-score">
                          <div className="score-circle">
                            <span className="score-value">
                              {visual_insights.visual_engagement_prediction.predicted_engagement_score}
                            </span>
                            <span className="score-label">/10</span>
                          </div>
                          {visual_insights.visual_engagement_prediction.confidence_level && (
                            <p className="confidence">
                              Confianza: <strong>{visual_insights.visual_engagement_prediction.confidence_level}</strong>
                            </p>
                          )}
                        </div>
                      )}
                      <div className="prediction-details">
                        {visual_insights.visual_engagement_prediction.visual_strengths && (
                          <div className="strengths">
                            <h4>✅ Fortalezas Visuales:</h4>
                            <ul>
                              {visual_insights.visual_engagement_prediction.visual_strengths.map((strength, idx) => (
                                <li key={idx} className="strength-item">{strength}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {visual_insights.visual_engagement_prediction.visual_weaknesses && (
                          <div className="weaknesses">
                            <h4>⚠️ Debilidades Visuales:</h4>
                            <ul>
                              {visual_insights.visual_engagement_prediction.visual_weaknesses.map((weakness, idx) => (
                                <li key={idx} className="weakness-item">{weakness}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {visual_insights.visual_engagement_prediction.improvement_suggestions && (
                          <div className="improvements">
                            <h4>💡 Sugerencias de Mejora:</h4>
                            {visual_insights.visual_engagement_prediction.improvement_suggestions.map((improvement, idx) => (
                              <div key={idx} className="improvement-item">
                                <p><strong>{improvement.suggestion}</strong></p>
                                {improvement.expected_impact && (
                                  <span className="impact-badge">{improvement.expected_impact}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Video Thumbnail Analysis */}
                {visual_insights.video_thumbnail_analysis && (
                  <div className="insight-card visual-card">
                    <h3>🎬 Análisis de Thumbnails de Video</h3>
                    <div className="thumbnail-analysis">
                      {visual_insights.video_thumbnail_analysis.thumbnail_effectiveness && (
                        <div className="effectiveness-badge">
                          <strong>Efectividad:</strong>
                          <span className={`badge badge-${visual_insights.video_thumbnail_analysis.thumbnail_effectiveness}`}>
                            {visual_insights.video_thumbnail_analysis.thumbnail_effectiveness}
                          </span>
                        </div>
                      )}
                      {visual_insights.video_thumbnail_analysis.predicted_scroll_stop_rate && (
                        <div className="scroll-stop-rate">
                          <div className="rate-meter">
                            <span className="rate-value">
                              {visual_insights.video_thumbnail_analysis.predicted_scroll_stop_rate}
                            </span>
                            <span className="rate-label">/10 Scroll-Stop Rate</span>
                          </div>
                        </div>
                      )}
                      {visual_insights.video_thumbnail_analysis.hook_elements && (
                        <div className="hook-elements">
                          <h4>🎣 Elementos de Hook:</h4>
                          <ul>
                            {visual_insights.video_thumbnail_analysis.hook_elements.map((hook, idx) => (
                              <li key={idx}>{hook}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {visual_insights.video_thumbnail_analysis.first_impression && (
                        <div className="first-impression">
                          <h4>Primera Impresión:</h4>
                          <p>{visual_insights.video_thumbnail_analysis.first_impression}</p>
                        </div>
                      )}
                      {visual_insights.video_thumbnail_analysis.recommendations && (
                        <div className="recommendations">
                          <h4>Recomendaciones:</h4>
                          {visual_insights.video_thumbnail_analysis.recommendations.map((rec, idx) => (
                            <div key={idx} className="recommendation-item">
                              <p><strong>{rec.recommendation}</strong></p>
                              {rec.expected_impact && (
                                <span className="impact">{rec.expected_impact}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Category Management Modal */}
      <CategoryManagementModal
        isOpen={showCategoryModal}
        onClose={() => setShowCategoryModal(false)}
        onCategoriesUpdated={fetchCategories}
      />
    </div>
  );
};

export default TrendsPage;
